import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getNonWorkDays,
  createNonWorkDay,
  updateNonWorkDay,
  NonWorkDay,
} from "util/network/Calendar";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";

export const toLocalDateStr = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const getDatesInRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = [];
  const cursor = new Date(start);
  const endTime = end.getTime();
  while (cursor.getTime() <= endTime) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
};

export type PendingRange = { start: Date; end: null } | null;

export const useCalendarTab = (year: number) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [calendarDays, setCalendarDays] = useState<NonWorkDay[]>([]);
  const [localChanges, setLocalChanges] = useState<Record<string, boolean>>({});
  const [pendingNames, setPendingNames] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [pendingRange, setPendingRange] = useState<PendingRange>(null);

  const dayMap = useMemo(
    () => Object.fromEntries(calendarDays.map((d) => [d.date.slice(0, 10), d])),
    [calendarDays],
  );

  const fetchAll = useCallback(async () => {
    const results = await Promise.all(
      Array.from({ length: 12 }, (_, i) => getNonWorkDays(token, year, i + 1)),
    );
    setCalendarDays(results.flat());
  }, [token, year]);

  useEffect(() => {
    setLocalChanges({});
    fetchAll().catch((err) => console.error("Failed to fetch calendar:", err));
  }, [fetchAll]);

  const toggleDate = useCallback(
    (dateStr: string) => {
      const day = dayMap[dateStr];
      const serverIsWorking = day?.isWorking ?? true;
      const currentIsWorking =
        dateStr in localChanges ? localChanges[dateStr] : serverIsWorking;
      const nextIsWorking = !currentIsWorking;
      if (nextIsWorking === serverIsWorking) {
        setLocalChanges((prev) => {
          const next = { ...prev };
          delete next[dateStr];
          return next;
        });
        setPendingNames((prev) => {
          const next = { ...prev };
          delete next[dateStr];
          return next;
        });
      } else {
        setLocalChanges((prev) => ({ ...prev, [dateStr]: nextIsWorking }));
      }
    },
    [dayMap, localChanges],
  );

  const [nameDialogState, setNameDialogState] = useState<{
    open: boolean;
    dates: string[];
    existingDates: string[];
    defaultName: string;
  }>({ open: false, dates: [], existingDates: [], defaultName: "" });

  const onTileClick = useCallback(
    (date: Date, event: React.MouseEvent) => {
      const dateStr = toLocalDateStr(date);
      const day = dayMap[dateStr];
      const serverIsWorking = day?.isWorking ?? true;
      const currentIsWorking =
        dateStr in localChanges ? localChanges[dateStr] : serverIsWorking;

      if (event.shiftKey) {
        if (!pendingRange) {
          setPendingRange({ start: date, end: null });
        } else {
          const startTime = pendingRange.start.getTime();
          const start = startTime <= date.getTime() ? pendingRange.start : date;
          const end = startTime <= date.getTime() ? date : pendingRange.start;
          const dates = getDatesInRange(start, end).map(toLocalDateStr);
          const existingDates = dates.filter((d) => {
            const existingDay = dayMap[d];
            const serverWorking = existingDay?.isWorking ?? true;
            return !(d in localChanges ? localChanges[d] : serverWorking);
          });
          setPendingRange(null);
          if (existingDates.length === dates.length) {
            dates.forEach((d) => toggleDate(d));
            return;
          }
          setNameDialogState({ open: true, dates, existingDates, defaultName: "" });
        }
        return;
      }

      if (pendingRange) {
        setPendingRange(null);
        return;
      }

      if (!currentIsWorking || (dateStr in localChanges && localChanges[dateStr])) {
        toggleDate(dateStr);
        return;
      }

      setNameDialogState({
        open: true,
        dates: [dateStr],
        existingDates: [],
        defaultName: day?.name ?? "",
      });
    },
    [dayMap, localChanges, pendingRange, toggleDate],
  );

  const confirmNameDialog = useCallback(
    (name: string, overwriteExisting: boolean) => {
      const { dates, existingDates } = nameDialogState;
      const existingSet = new Set(existingDates);
      const datesToApply = overwriteExisting ? dates : dates.filter((d) => !existingSet.has(d));
      const datesToClose = datesToApply.filter((d) => {
        const day = dayMap[d];
        const serverIsWorking = day?.isWorking ?? true;
        return d in localChanges ? localChanges[d] : serverIsWorking;
      });
      if (datesToClose.length > 0) {
        setLocalChanges((prev) => {
          const next = { ...prev };
          datesToClose.forEach((d) => { next[d] = false; });
          return next;
        });
      }
      setPendingNames((prev) => {
        const next = { ...prev };
        datesToApply.forEach((d) => { next[d] = name; });
        return next;
      });
      setNameDialogState({ open: false, dates: [], existingDates: [], defaultName: "" });
    },
    [nameDialogState, dayMap, localChanges],
  );

  const cancelNameDialog = useCallback(() => {
    setNameDialogState({ open: false, dates: [], existingDates: [], defaultName: "" });
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const ops: Promise<NonWorkDay | void>[] = [];
      Object.entries(localChanges).forEach(([dateStr, isWorking]) => {
        const day = dayMap[dateStr];
        const name = pendingNames[dateStr] ?? day?.name ?? "Custom shutdown";
        if (day) ops.push(updateNonWorkDay(token, day.id, isWorking, name));
        else if (!isWorking) ops.push(createNonWorkDay(token, dateStr, name, false));
      });
      Object.entries(pendingNames)
        .filter(([dateStr]) => !(dateStr in localChanges))
        .forEach(([dateStr, name]) => {
          const day = dayMap[dateStr];
          if (day && !day.isWorking) ops.push(updateNonWorkDay(token, day.id, false, name));
        });
      await Promise.all(ops);
      await fetchAll();
      setLocalChanges({});
      setPendingNames({});
      addToast({ type: "success", message: "Calendar changes saved successfully" });
    } catch (err) {
      console.error("Failed to save calendar changes:", err);
      addToast({ type: "error", message: "Failed to save calendar changes" });
    } finally {
      setSaving(false);
    }
  }, [localChanges, pendingNames, dayMap, token, fetchAll, addToast]);

  const hasChanges = useMemo(
    () => Object.keys(localChanges).length > 0 || Object.keys(pendingNames).length > 0,
    [localChanges, pendingNames],
  );

  return {
    dayMap,
    localChanges,
    pendingNames,
    saving,
    hasChanges,
    onTileClick,
    save,
    pendingRange,
    nameDialogState,
    confirmNameDialog,
    cancelNameDialog,
  };
};
